// @flow
import React, { PureComponent } from 'react';
import { withRouter } from 'react-router-dom';

import { withHelmet } from 'infrastructure/seo/Helmet';
import { STATUS_SUCCESS } from 'constants/statusConstants';

import BillboardCard from 'components/billboard/BillboardCard';
import SideBillboardCard from 'components/billboard/SideBillboardCard';
import NewsByTheme from 'components/newsletter/NewsByTheme';
import SideBar from 'components/sidebar/Sidebar';
import Themes from 'containers/newsletter/ThemesContainer';
import MenuElement from 'components/header/MenuElement';
import Breadcrumb from 'components/fragments/Breadcrumb';
import DropdownRadioInput from 'components/fragments/input/DropdownRadioInput';
import Loading from 'components/fragments/Loading';
import LiveScore from 'containers/fragments/LiveScoreContainer';

import { getMetaBySlug } from 'utils/seoUtils';

import type { State as SidebarType } from 'reducers/sidebarReducer';
import type { ArticleType, CategoryArticleType } from 'types/Article';
import type { Status } from 'types/Status';
import type { RouterHistory } from 'react-router-dom';

export type DispatchProps = {
  fetchArticlesStaticCategories: () => void,
  fetchGenericSidebar: () => void,
  fetchPageList: (slug: string) => void,
};

export type StateProps = {
  categoriesArticle: CategoryArticleType[],
  sidebar: SidebarType,
  status: string,
  featured_media: {
    src: string,
    legend: string,
  },
  banniere: string,
  placeholder: string,
  settingsStatus: Status,
  organization: string,
  rencontresLive: number,
  title: string
};

type State = {
  currentPage: number,
  menuItems: { id: number, size: number }[],
  isMobile: boolean,
  hiddenMenuItems: number[],
  menuElements: MenuElement[],
  menuLinkX: number,
  labelPlus: string,
  news: ArticleType[],
};

type Props = StateProps & DispatchProps & {
  history: RouterHistory,
};

class Newsletter extends PureComponent<Props, State> {
  _menuElements: MenuElement[];
  _menuLink: ?HTMLElement;
  props: Props;
  state: State = {
    currentPage: 1,
    menuItems: [],
    isMobile: false,
    hiddenMenuItems: [],
    menuElements: [],
    menuLinkX: 0,
    labelPlus: 'Plus',
    news: [],
  };

  componentDidMount() {
    const {
      fetchArticlesStaticCategories,
      fetchGenericSidebar,
      fetchPageList,
    } = this.props;
    const { pathname } = window.location;
    const slug = pathname.split('/')[1];

    fetchArticlesStaticCategories();
    fetchGenericSidebar();
    fetchPageList(slug);
    window.addEventListener('resize', this.updateDimensions.bind(this));
    this.updateDimensions();
  }

  componentDidUpdate(prevProps) {
    const { categoriesArticle } = this.props;
    const { news } = this.state;

    if (news.length === 0 && news.length < categoriesArticle.length) {
      const news = this.getNews();

      this.updateDimensions();

      if (news && news.length > 0) {
        this.setState({ news });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions.bind(this));
  }

  resetMenuElementRef = (c: ?MenuElement) => {
    this._menuElements = [];
    c && this.addMenuElementRef(c);
  };

  addMenuElementRef = (c: ?MenuElement) => {
    c && this._menuElements.push(c);
  };

  isDisplayed = (id: number) => {
    const { hiddenMenuItems } = this.state;
    return !hiddenMenuItems.includes(id);
  };

  updateDimensions() {
    const menuLink = this._menuLink;
    const {
      menuLinkX: menuLinkXState,
      isMobile,
      hiddenMenuItems,
    } = this.state;
    if (menuLink) {
      const menuLinkBounds = menuLink.getBoundingClientRect();
      const menuLinkX = menuLinkBounds.left;

      if (menuLinkX !== menuLinkXState) this.setState({ menuLinkX });
      const maxWidth = menuLink.offsetWidth;

      const width_menu_links = (this._menuElements && this._menuElements.length) > 0
        ? this._menuElements.reduce((total, item) => (item ? total + item.getSize() * 1.1 : total), 0)
        : 0;

      const totalWidth = width_menu_links;

      let currentWidth = totalWidth;
      let deleted = [];
      if (this._menuElements) {
        for (let i = this._menuElements.length - 1; i > 0; i--) {
          if (currentWidth > maxWidth || window.innerWidth <= 767) {
            if (this._menuElements[i]) currentWidth -= this._menuElements[i].getSize();
            deleted = [...deleted, i];
          }
        }
      }
      if (isMobile) this.setState({ isMobile: false });
      if (
        (deleted.length !== hiddenMenuItems.length &&
          (deleted.length > 0 || hiddenMenuItems.length > 0) &&
          !deleted.every(id => hiddenMenuItems.includes(id))) ||
        !hiddenMenuItems.every(id => deleted.includes(id))
      ) {
        this.setState({ hiddenMenuItems: deleted });
      }
    }
  }

  handleDropdownInputChange = (value: string) => {
    const { history } = this.props;

    const redirection = value === 'toutes-les-thematiques'
      ? `actualites`
      : `actualites/${value}`;

    history.push(redirection);
  };

  renderMenuElements = () => {
    const { categoriesArticle } = this.props;
    const { menuLinkX } = this.state;

    if (categoriesArticle.length > 0) {
      const options = [];

      options.push({ label: 'Toutes les thématiques', value: 'toutes-les-thematiques' });

      categoriesArticle.forEach((category) => {
        const { name: label, slug: value } = category;
        options.push({ label, value });
      });

      return (
        <DropdownRadioInput
          noBorder
          radio
          options={options}
          inputChanged={this.handleDropdownInputChange}/>
      );
    }
    return categoriesArticle
      && categoriesArticle.length > 0
      && (
          categoriesArticle.map((categorie, index) => (
            <MenuElement
              key={categorie.slug}
              urlType="category"
              url={`/actualites/${categorie.slug}`}
              title={categorie.name}
              isMobile={true}
              ref={index === 0 ? this.resetMenuElementRef : this.addMenuElementRef}
              id={index}
              isDisplayed={this.isDisplayed}
              menuX={menuLinkX}
            />
          )
        )
      );
  };

  renderNews = (news: ArticleType[]): any => {
    return news.splice(0, 3)
      .map((news, index): any => (
        <SideBillboardCard key={index} content={news} />
      ));
  };

  renderNewsByTheme = () => {
    const { categoriesArticle } = this.props;
    return categoriesArticle && categoriesArticle.length > 0 && categoriesArticle.map((category, index) => {
      if (category.items.length > 0) {
        return (
          <NewsByTheme key={index} news={category.items} theme={category.name} url={`/actualites/${category.slug}`} />
        );
      }
      return null;
    });
  };

  renderSidebar = () => {
    const { sidebar } = this.props;
    return <SideBar sidebarContent={sidebar.data} status={sidebar.status} />;
  };

  /**
   * Récupérer toutes les news puis :
   * - les trier par date
   * - enlever les doublons
   * - retourner uniquement les 6 dernières
   */
  getNews = (): ArticleType[] => {
    const { categoriesArticle } = this.props;

    let news = [];
    let ids = [];
    if (categoriesArticle && categoriesArticle.length > 0) {
      categoriesArticle.forEach(cat => {
        if (cat.items && cat.items.length > 0) {
          cat.items.forEach(actu => {
            news.push(actu);
          });
        }
      });
    }

    return news.filter(item => (
        ids.indexOf(item.link) === -1 ? ids.push(item.link) : false
    )).sort((a, b) => {
      const x = a.date.toString();
      const y = b.date.toString();

      return y < x ? -1 : y > x ? 1 : 0;
    }).slice(0, 6);
  };

  render() {
    const { 
      organization,
      banniere,
      placeholder,
      featured_media: { src },
      settingsStatus,
      rencontresLive,
      title
    } = this.props;

    let bannerClassName = "banner banner--themes";
    if (rencontresLive) {
      bannerClassName = "banner banner--themes banner--live-score";
    }
    const { news } = this.state;
    if (settingsStatus !== STATUS_SUCCESS) {
      return <Loading />;
    }
    return (
      <>
        <section className={bannerClassName}>
          <div
            className="banner__thumbnail img-secondary"
            style={{ backgroundImage: `url(${src || banniere || placeholder})` }}
          />
          <div className="container">
            <Breadcrumb elements={[{ name: 'Accueil', to: '/' }, 'Actualités']} isDark={true} />
            <h1 className="ft-h2">{title || `Actualités Rugby`} - {organization}</h1>
            <ul className="home-slider__links ft-500" ref={c => (this._menuLink = c)}>
              <li><span className="ft-up">Par thématiques :</span></li>
              {this.renderMenuElements()}
            </ul>
          </div>
          <LiveScore />
        </section>
        <div className="container">
          <div className="row">
            <div className="col col-md-8 col-lg-9 bg-overflow">
              <section>
                <div className="row">
                  <div className="col col-lg-8on9 banner-over banner-over--themes">
                    {news.length > 0 &&
                    <BillboardCard content={news[0]} containerStyle={'card--billboard-actu mb-4 mb-md-2 mb-lg-5'} /> }
                  </div>
                  <div className="col col-lg-8on9">
                    <div className="news-slider mb-6 mb-md-7">
                      {news.length > 0 &&
                      <div className="news-slider__wrapper">{this.renderNews(news.slice(1))}</div> }
                    </div>
                  </div>
                </div>
                <Themes />
                {this.renderNewsByTheme()}
              </section>
            </div>
            {this.renderSidebar()}
          </div>
        </div>
      </>
    );
  }
}

export default withHelmet(withRouter(Newsletter))((Helmet, pageProps) => {
  const { organization } = pageProps;
  const meta = getMetaBySlug(pageProps.location.pathname);
  return (
    <Helmet>
      <title>{meta.title(organization)}</title>
      <meta name={'description'} content={meta.description()} />
      <meta name={'keywords'} content={`Actualités ${organization}`} />
      <meta property={'og:title'} content={meta.keywords(organization)} />
      <meta property={'og:description'} content={meta.description()} />
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
